import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/hooks';
import { addLocalePrefix } from '../i18n/routing';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  steps: string[];
  blogUrl: string;
  emoji: string;
}

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  steps,
  blogUrl,
  emoji
}) => {
  const locale = useLocale();
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
        <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <span className="text-3xl">{emoji}</span>
                {title}
              </h2>
              <p className="mt-2 text-primary-50 text-sm">{description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label={t('infoModal.closeAria')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 text-gray-800 dark:text-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            {t('infoModal.howToPlay')}
          </h3>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start">
                <span className="flex-shrink-0 w-7 h-7 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                  {index + 1}
                </span>
                <span className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed">{step}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800 space-y-3">
            <a
              href={addLocalePrefix(blogUrl, locale)}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              {t('infoModal.moreInfo')}
            </a>
            <button
              onClick={onClose}
              className="block w-full text-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100 font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {t('infoModal.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
